import { useEffect, useState } from 'react';

import {
  getApiData,
  isApiError,
  makeApiError,
  makeApiSuccess,
} from 'scenes/common/helpers/apiData';
import {
  ApiData,
  RecentInsurerOptions,
  RefDataType,
} from 'scenes/common/types';
import referenceDataApi from 'scenes/landlord/api/referenceData/referenceDataApi';

import { ReferenceData } from 'scenes/landlord/types';

export const useReferenceData = (): ApiData<ReferenceData> => {
  const [recentInsurerOptions, setRecentInsurerOptions] = useState<
    RecentInsurerOptions
  >({
    dontKnowOption: { value: '', label: '' },
    otherOption: { value: '', label: '' },
    notInsuredOption: { value: '', label: '' },
    mostCommonInsurerList: [],
    otherInsurerList: [],
  });
  const [buildingTypes, setBuildingTypes] = useState<RefDataType[]>([]);
  const [buildYears, setBuildYears] = useState<RefDataType[]>([]);
  const [roofMaterials, setRoofMaterials] = useState<RefDataType[]>([]);
  const [wallMaterials, setWallMaterials] = useState<RefDataType[]>([]);
  const [homeSecurities, setHomeSecurities] = useState<RefDataType[]>([]);
  const [mortgageProviderOptions, setMortgageProviderOptions] = useState<
    RefDataType[]
  >([]);

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let componentMounted = true;
    const referenceDataApiInstance = referenceDataApi();

    referenceDataApiInstance
      .getInsurers()
      .then((insurerData) => {
        if (componentMounted) {
          if (isApiError(insurerData)) {
            setHasError(true);
          } else {
            setRecentInsurerOptions(getApiData(insurerData));
          }
        }
      })
      .catch(() => setHasError(true));

    referenceDataApiInstance
      .getBuildingTypes()
      .then((buildingTypeOptions) => {
        if (componentMounted) {
          if (isApiError(buildingTypeOptions)) {
            setHasError(true);
          } else {
            setBuildingTypes(getApiData(buildingTypeOptions));
          }
        }
      })
      .catch(() => setHasError(true));

    referenceDataApiInstance
      .getBuildYear()
      .then((buildYearOptions) => {
        if (componentMounted) {
          if (isApiError(buildYearOptions)) {
            setHasError(true);
          } else {
            setBuildYears(getApiData(buildYearOptions));
          }
        }
      })
      .catch(() => setHasError(true));

    referenceDataApiInstance
      .getRoofMaterial()
      .then((roofMaterialOptions) => {
        if (componentMounted) {
          if (isApiError(roofMaterialOptions)) {
            setHasError(true);
          } else {
            setRoofMaterials(getApiData(roofMaterialOptions));
          }
        }
      })
      .catch(() => setHasError(true));

    referenceDataApiInstance
      .getWallMaterial()
      .then((wallMaterialOptions) => {
        if (componentMounted) {
          if (isApiError(wallMaterialOptions)) {
            setHasError(true);
          } else {
            setWallMaterials(getApiData(wallMaterialOptions));
          }
        }
      })
      .catch(() => setHasError(true));

    referenceDataApiInstance
      .getHomeSecurity()
      .then((homeSecurityOptions) => {
        if (componentMounted) {
          if (isApiError(homeSecurityOptions)) {
            setHasError(true);
          } else {
            setHomeSecurities(getApiData(homeSecurityOptions));
          }
        }
      })
      .catch(() => setHasError(true));

    referenceDataApiInstance
      .getMortgageProviders()
      .then((providerData) => {
        if (componentMounted) {
          if (isApiError(providerData)) {
            setHasError(true);
          } else {
            setMortgageProviderOptions(getApiData(providerData));
          }
        }
      })
      .catch(() => setHasError(true));

    return () => {
      componentMounted = false;
    };
  }, []);

  return hasError
    ? makeApiError(`Can't get reference data`)
    : makeApiSuccess<ReferenceData>({
        recentInsurerOptions,
        buildingTypes,
        buildYears,
        roofMaterials,
        wallMaterials,
        homeSecurities,
        mortgageProviderOptions,
      });
};
